import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
    .required('New password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ChangePassword: React.FC = () => {
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });
  const [oldPasswordError, setOldPasswordError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: yupResolver(schema),
  });

  const newPassword = watch('newPassword');

  // Password strength checker
  React.useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(0);
      setPasswordChecks({
        length: false,
        lowercase: false,
        uppercase: false,
        number: false,
        special: false,
      });
      return;
    }

    const checks = {
      length: newPassword.length >= 8,
      lowercase: /[a-z]/.test(newPassword),
      uppercase: /[A-Z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
    };

    setPasswordChecks(checks);

    const strength = Object.values(checks).filter(Boolean).length;
    setPasswordStrength((strength / 5) * 100);
  }, [newPassword]);

  const handleTogglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onSubmit = async (data: ChangePasswordForm) => {
    setLoading(true);
    setOldPasswordError(null);
    try {
      // Simulate API call
      const response = await authAPI.changePassword(data.currentPassword, data.newPassword);

      // If API returns error in response.data (not thrown)
      if (
        response?.status === 400 &&
        (
          response?.data?.responseMessageCode === "AUTH_INVALID_OLD_PASSWORD" ||
          response?.data?.messageCode === "AUTH_INVALID_OLD_PASSWORD" ||
          response?.data?.responseData?.messageCode === "AUTH_INVALID_OLD_PASSWORD"
        )
      ) {
        setOldPasswordError(
          response?.data?.responseData?.message ||
          response?.data?.message ||
          "Incorrect old password"
        );
        // Do NOT show toast for this case
        return;
      }

      toast.success('Password changed successfully!');
      handleReset();
    } catch (error: any) {
      // Handle error thrown by axios (error.response)
      if (
        error?.response?.status === 400 &&
        (
          error?.response?.data?.responseMessageCode === "AUTH_INVALID_OLD_PASSWORD" ||
          error?.response?.data?.messageCode === "AUTH_INVALID_OLD_PASSWORD" ||
          error?.response?.data?.responseData?.messageCode === "AUTH_INVALID_OLD_PASSWORD"
        )
      ) {
        setOldPasswordError(
          error?.response?.data?.responseData?.message ||
          error?.response?.data?.message ||
          "Incorrect old password"
        );
        // Do NOT show toast for this case
        return;
      }
      toast.error('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    // Reset form with empty default values
    reset({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

    // Reset all other states
    setPasswordStrength(0);
    setPasswordChecks({
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      special: false,
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false,
    });
    setOldPasswordError(null);
  };

  return (
    <Box sx={{mx: 'auto', maxWidth: 600}}>
      <Typography variant="h5" gutterBottom sx={{ mb: 4, fontSize: '1.1rem', fontWeight: 800, letterSpacing: .4 }}>
        Change Password
      </Typography>

      <Box sx={{
        display: 'flex',
        gap: 3,
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Main Form Card */}
        <Box sx={{ flex: { xs: 'none', md: 2 } }}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 3 }}>

              <Alert severity="info" sx={{ mb: 2, p:0 }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>Security Tip</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Choose a strong password that you haven't used elsewhere. A strong password helps protect your account from unauthorized access.
                  </Typography>
                </Box>
              </Alert>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ display: 'grid', gap: 3 }}>
                  <Controller
                    name="currentPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Current Password"
                        placeholder="Enter current password"
                        InputLabelProps={{ sx: { fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: .6, fontWeight: 600 } }}
                        inputProps={{ sx: { fontSize: '.8rem', fontWeight: 600, '::placeholder': { fontSize: '.75rem', opacity: 0.8 } } }}
                        type={showPasswords.current ? 'text' : 'password'}
                        fullWidth
                        error={!!errors.currentPassword || !!oldPasswordError}
                        helperText={oldPasswordError ? oldPasswordError : errors.currentPassword?.message}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Tooltip title={showPasswords.current ? 'Hide password' : 'Show password'}>
                              <IconButton
                                onClick={() => handleTogglePasswordVisibility('current')}
                                edge="end"
                                size="small"
                              >
                                {showPasswords.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="newPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="New Password"
                        placeholder="Create new password"
                        InputLabelProps={{ sx: { fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: .6, fontWeight: 600 } }}
                        inputProps={{ sx: { fontSize: '.8rem', fontWeight: 600, '::placeholder': { fontSize: '.75rem', opacity: 0.8 } } }}
                        type={showPasswords.new ? 'text' : 'password'}
                        fullWidth
                        error={!!errors.newPassword}
                        helperText={errors.newPassword?.message}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Tooltip title={showPasswords.new ? 'Hide password' : 'Show password'}>
                              <IconButton
                                onClick={() => handleTogglePasswordVisibility('new')}
                                edge="end"
                                size="small"
                              >
                                {showPasswords.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Confirm New Password"
                        placeholder="Re-enter new password"
                        InputLabelProps={{ sx: { fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: .6, fontWeight: 600 } }}
                        inputProps={{ sx: { fontSize: '.8rem', fontWeight: 600, '::placeholder': { fontSize: '.75rem', opacity: 0.8 } } }}
                        type={showPasswords.confirm ? 'text' : 'password'}
                        fullWidth
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                          endAdornment: (
                            <Tooltip title={showPasswords.confirm ? 'Hide password' : 'Show password'}>
                              <IconButton
                                onClick={() => handleTogglePasswordVisibility('confirm')}
                                edge="end"
                                size="small"
                              >
                                {showPasswords.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                    )}
                  />
                  {newPassword ? (
                    <>
                      <Box sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ mb: 0, display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase' }}>
                          Requirements
                        </Typography>

                        <Box sx={{ display: 'grid', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {passwordChecks.length ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              At least 8 characters
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {passwordChecks.lowercase ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              One lowercase letter
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {passwordChecks.uppercase ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              One uppercase letter
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {passwordChecks.number ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              One number
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {passwordChecks.special ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              One special character
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                      disabled={loading}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={loading || passwordStrength < 80}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: 'secondary.main',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        }
                      }}
                    >
                      {loading ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </Box>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePassword;